/**
 * The spoken text for the pain scale and the body map.
 *
 * **Every sentence is stored whole, not composed from a template.** German prepositions and
 * articles do not compose — *im Bauch*, *am linken Arm*, *im unteren Rücken*, *an der Hüfte* — and a
 * template plus rules would produce wrong German at the moment it matters most, to a listener who
 * may be a paramedic. Storing 20 sentences is cheaper than being subtly wrong under pressure.
 *
 * Swiss orthography throughout; `ss`, never the sharp s.
 */

/** Spoken, not printed: "Schmerzen, Stufe sieben" reads better than "Stufe 7". */
export const PAIN_LEVEL_WORDS: readonly string[] = [
  'null',
  'eins',
  'zwei',
  'drei',
  'vier',
  'fünf',
  'sechs',
  'sieben',
  'acht',
  'neun',
  'zehn',
];

export const PAIN_MAX = 10;

export function painUtterance(level: number): string {
  const word = PAIN_LEVEL_WORDS[level];
  if (word === undefined) throw new Error(`pain level out of range: ${level}`);
  if (level === 0) return 'Ich habe keine Schmerzen.';
  return `Schmerzen, Stufe ${word}.`;
}

export type BodySide = 'front' | 'back';

export interface BodyRegion {
  id: string;
  side: BodySide;
  /** German label on the button. */
  label: string;
  /** The complete sentence, correct on its own. */
  utterance: string;
}

/**
 * Body regions as a **labelled grid, not a tappable figure** — and that is a geometry result, not a
 * preference.
 *
 * The design asks for a front/back SVG figure with large regions. Measured against the space the
 * frame actually leaves (708 x 326 px for this panel), a 200 x 420 standing figure renders at a
 * scale of 0.776, so a 16 mm target would need 107 viewBox units. That allows **1.9 horizontal
 * zones** — not enough for left and right — and 3.9 vertical. A figure here could only ever have
 * had ~4 mm targets, which is unusable for someone with impaired motor control and would have
 * looked perfectly plausible on screen.
 *
 * A 3 x 3 grid in the same space gives tiles of 38 x 21 mm. Written language is intact, so labels
 * work; and the grid is the same interaction the person already learns on Saetze. What is lost is
 * pointing at a picture, which matters least to the case this panel exists for — one tap, no
 * typing, when pain is bad enough that typing has stopped.
 *
 * Nine slots per side, so nothing scrolls and nothing hides.
 */
export const BODY_REGIONS: readonly BodyRegion[] = [
  // Front — nine, ordered head to foot so the grid reads top-left to bottom-right.
  { id: 'kopf', side: 'front', label: 'Kopf', utterance: 'Ich habe Schmerzen im Kopf.' },
  { id: 'hals', side: 'front', label: 'Hals', utterance: 'Ich habe Schmerzen im Hals.' },
  { id: 'brust', side: 'front', label: 'Brust', utterance: 'Ich habe Schmerzen in der Brust.' },
  { id: 'bauch', side: 'front', label: 'Bauch', utterance: 'Ich habe Schmerzen im Bauch.' },
  { id: 'huefte', side: 'front', label: 'Hüfte', utterance: 'Ich habe Schmerzen an der Hüfte.' },
  { id: 'arm-links', side: 'front', label: 'Arm links', utterance: 'Ich habe Schmerzen am linken Arm.' },
  { id: 'arm-rechts', side: 'front', label: 'Arm rechts', utterance: 'Ich habe Schmerzen am rechten Arm.' },
  { id: 'bein-links', side: 'front', label: 'Bein links', utterance: 'Ich habe Schmerzen am linken Bein.' },
  { id: 'bein-rechts', side: 'front', label: 'Bein rechts', utterance: 'Ich habe Schmerzen am rechten Bein.' },

  // Back — eight. The ninth slot stays empty rather than inventing a region nobody points at.
  { id: 'hinterkopf', side: 'back', label: 'Hinterkopf', utterance: 'Ich habe Schmerzen am Hinterkopf.' },
  { id: 'nacken', side: 'back', label: 'Nacken', utterance: 'Ich habe Schmerzen im Nacken.' },
  { id: 'schultern', side: 'back', label: 'Schultern', utterance: 'Ich habe Schmerzen in den Schultern.' },
  { id: 'ruecken-oben', side: 'back', label: 'Rücken oben', utterance: 'Ich habe Schmerzen im oberen Rücken.' },
  { id: 'ruecken-unten', side: 'back', label: 'Rücken unten', utterance: 'Ich habe Schmerzen im unteren Rücken.' },
  { id: 'gesaess', side: 'back', label: 'Gesäss', utterance: 'Ich habe Schmerzen am Gesäss.' },
  { id: 'bein-hinten-links', side: 'back', label: 'Bein links', utterance: 'Ich habe Schmerzen hinten am linken Bein.' },
  { id: 'bein-hinten-rechts', side: 'back', label: 'Bein rechts', utterance: 'Ich habe Schmerzen hinten am rechten Bein.' },
];

/** Nine slots per side: one screen, no scrolling, no paging. */
export const REGIONS_PER_SIDE = 9;

export const regionsFor = (side: BodySide): BodyRegion[] =>
  BODY_REGIONS.filter((region) => region.side === side);
